import {
    Box,
    Flex,
    Image,
    Popover,
    PopoverContent,
    PopoverTrigger,
    Portal,
    Text,
    chakra,
    keyframes,
} from '@chakra-ui/react';
import { IoCheckmark } from 'react-icons/io5';

import { Button } from '@/components/v4';
import { SETUP_BLUE, SETUP_GREEN } from './constants';

const ping = keyframes`
    75%, 100% { transform: scale(1.8); opacity: 0; }
`;

// First and last labels stay inside the bar; the rest centre on their step.
const edgeAlignment = (isFirst, isLast) => {
    if (isFirst) return { left: 0 };
    if (isLast) return { right: 0 };
    return { left: '50%', transform: 'translateX(-50%)' };
};

// Fixed so the card doesn't stretch with the number of services. Seven 24px
// slots with 8px gaps fill the row exactly; past that the rest collapse into
// a "+N" slot.
const TOOLTIP_WIDTH = 248;
const MAX_SERVICE_SLOTS = 7;

const serviceSlotStyles = {
    w: '24px',
    h: '24px',
    flexShrink: 0,
    align: 'center',
    justify: 'center',
};

const serviceTitle = (service) =>
    service.added ? service.label : `${service.label} - not added yet`;

const circleStyles = {
    position: 'relative',
    w: '28px',
    h: '28px',
    borderRadius: 'full',
    borderWidth: '2px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '11px',
    fontWeight: 'semibold',
};

/**
 * (3145/3154/3155) One milestone on the setup bar, with a hover card
 * explaining the step, the services it applies to and what to do about it.
 * The current step pulses and, when it has an action, is clickable.
 */
const SetupStep = ({
    step,
    index,
    stepCount,
    isCurrent,
    stepServices,
    darkMode,
    onAction,
    onLearnHow,
}) => {
    const isFirst = index === 0;
    const isLast = index === stepCount - 1;
    const muted = darkMode ? 'gray.400' : 'gray.500';
    const alignment = edgeAlignment(isFirst, isLast);

    // A current step with nothing to act on (Get numbers without public pool
    // numbers) still stands out, but isn't a button.
    const CurrentTag = step.action ? chakra.button : chakra.span;
    const currentActionProps = step.action
        ? { type: 'button', onClick: onAction, cursor: 'pointer' }
        : {};

    const isOverflowing = stepServices.length > MAX_SERVICE_SLOTS;
    const visibleServices = isOverflowing
        ? stepServices.slice(0, MAX_SERVICE_SLOTS - 1)
        : stepServices;
    const collapsedServices = isOverflowing
        ? stepServices.slice(MAX_SERVICE_SLOTS - 1)
        : [];

    const pendingCircle = {
        bg: step.done ? SETUP_GREEN : darkMode ? '#1a1a1a' : 'white',
        borderColor: step.done ? SETUP_GREEN : darkMode ? '#4b5563' : '#d1d5db',
        color: step.done ? 'white' : darkMode ? '#9ca3af' : '#6b7280',
    };

    return (
        <Popover
            trigger="hover"
            placement="bottom"
            strategy="fixed"
            gutter={30}
            openDelay={100}
            isLazy>
            <PopoverTrigger>
                <Box
                    position="absolute"
                    top="50%"
                    left={`${(index / (stepCount - 1)) * 100}%`}
                    transform="translate(-50%, -50%)"
                    zIndex={isCurrent ? 2 : 1}
                    tabIndex={isCurrent && step.action ? undefined : 0}
                    aria-label={`${step.label}: ${
                        step.done ? 'done' : 'not done'
                    }`}
                    data-testid={`setup-step-${step.key}`}>
                    {isCurrent && (
                        <Box
                            position="absolute"
                            inset={0}
                            borderRadius="full"
                            bg={SETUP_BLUE}
                            opacity={0.45}
                            pointerEvents="none"
                            animation={`${ping} 1s cubic-bezier(0, 0, 0.2, 1) infinite`}
                            sx={{
                                '@media (prefers-reduced-motion: reduce)': {
                                    animation: 'none',
                                },
                            }}
                        />
                    )}
                    {isCurrent ? (
                        <CurrentTag
                            {...currentActionProps}
                            title={step.label}
                            sx={circleStyles}
                            bg={SETUP_BLUE}
                            borderColor={SETUP_BLUE}
                            color="white">
                            {index + 1}
                        </CurrentTag>
                    ) : (
                        <Box sx={circleStyles} {...pendingCircle}>
                            {step.done ? (
                                <IoCheckmark size={14} aria-hidden />
                            ) : (
                                index + 1
                            )}
                        </Box>
                    )}
                    {isCurrent ? (
                        <CurrentTag
                            {...currentActionProps}
                            position="absolute"
                            top="36px"
                            mt="-2px"
                            whiteSpace="nowrap"
                            fontSize="10px"
                            fontWeight="semibold"
                            px={2}
                            py="2px"
                            borderRadius="full"
                            bg={SETUP_BLUE}
                            color="white"
                            {...alignment}>
                            {step.label}
                        </CurrentTag>
                    ) : (
                        <Text
                            as="span"
                            position="absolute"
                            top="36px"
                            whiteSpace="nowrap"
                            fontSize="11px"
                            color={
                                step.done
                                    ? darkMode
                                        ? 'gray.200'
                                        : 'gray.800'
                                    : muted
                            }
                            {...alignment}>
                            {step.label}
                        </Text>
                    )}
                </Box>
            </PopoverTrigger>
            <Portal>
                <PopoverContent
                    rootProps={{ zIndex: 'tooltip' }}
                    w={`${TOOLTIP_WIDTH}px`}
                    maxW="calc(100vw - 16px)"
                    px={3}
                    py={2}
                    borderRadius="lg"
                    boxShadow="lg"
                    bg={darkMode ? '#1f1f1f' : 'white'}
                    borderColor={darkMode ? '#374151' : 'gray.200'}
                    _focus={{ outline: 'none' }}>
                    <Text
                        fontSize="xs"
                        fontWeight="semibold"
                        color={darkMode ? 'white' : 'gray.900'}>
                        {step.label}
                    </Text>
                    <Text fontSize="11px" mt="2px" color={muted}>
                        {step.description}
                    </Text>
                    {stepServices.length > 0 && (
                        <>
                            <Text
                                fontSize="10px"
                                fontWeight="semibold"
                                mt={2}
                                mb="6px"
                                color={muted}>
                                Applies to
                            </Text>
                            <Flex align="center" gap={2}>
                                {visibleServices.map((service) => (
                                    <Flex
                                        key={service.key}
                                        {...serviceSlotStyles}
                                        opacity={service.added ? 1 : 0.35}
                                        title={serviceTitle(service)}>
                                        <Image
                                            src={service.icon}
                                            alt={service.label}
                                            boxSize="20px"
                                            objectFit="contain"
                                        />
                                    </Flex>
                                ))}
                                {collapsedServices.length > 0 && (
                                    <Flex
                                        {...serviceSlotStyles}
                                        borderRadius="md"
                                        bg={darkMode ? 'gray.700' : 'gray.100'}
                                        fontSize="10px"
                                        fontWeight="semibold"
                                        color={muted}
                                        title={collapsedServices
                                            .map(serviceTitle)
                                            .join('\n')}>
                                        +{collapsedServices.length}
                                    </Flex>
                                )}
                            </Flex>
                        </>
                    )}
                    {/* The Learn how slot. Until help & training exists it
                        carries the step's "Go to …" action. */}
                    {step.action && (
                        <Button
                            type="button"
                            onClick={onLearnHow}
                            mt={2}
                            w="full"
                            size="xs"
                            borderRadius="md"
                            fontSize="11px"
                            fontWeight="semibold"
                            bg={SETUP_BLUE}
                            color="white"
                            _hover={{ opacity: 0.9 }}>
                            {step.action.label}
                        </Button>
                    )}
                    {step.note && (
                        <Text
                            fontSize="11px"
                            fontWeight="semibold"
                            mt={2}
                            color={darkMode ? 'gray.200' : 'gray.700'}>
                            {step.note}
                        </Text>
                    )}
                </PopoverContent>
            </Portal>
        </Popover>
    );
};

export default SetupStep;
